import * as React from "react";
import { useLocation } from "wouter";
import { Target, RefreshCcw, Recycle, ArrowRight, ArrowLeft, Loader2, Package, MapPin } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { useSpaces } from "@/hooks/useSpaces.js";

/** The three goal types offered at creation (routine is NOT a goal type). */
type CreateGoalType = "find_home" | "reorganize" | "declutter";

const INTENT_CARDS: { value: CreateGoalType; label: string; hint: string; icon: React.ElementType; iconClass: string; next: string }[] = [
  { value: "find_home", label: "Find a home", hint: "Decide where specific things should live", icon: Target, iconClass: "text-primary", next: "Pick the items → we'll suggest a spot → try it for 2 weeks → keep or put back." },
  { value: "reorganize", label: "Reorganize a space", hint: "Rework how a room or container is organized", icon: RefreshCcw, iconClass: "text-primary", next: "Pick the space → snapshot what's there → try container-level moves → keep or put back." },
  { value: "declutter", label: "Declutter", hint: "Review unused things and let them go", icon: Recycle, iconClass: "text-sage-deep", next: "Review unused items → decide keep / donate / sell / toss." },
];

/**
 * Intent-first goal creation (Workstream C).
 *
 * Step 1: pick an intent (three goal types — routine is a separate flow, not a
 *         goal type). Step 2: a per-type form that asks only what that intent
 *         needs and previews what happens next.
 *
 * Deep-link entry points supported by the parent (OrganizeGoalsPage):
 *   - ?suggest_item / detection-nudge → open directly on the Find-a-home form,
 *     prefilled (via `initialType="find_home"` + `initialTitle`).
 *   - ?type=<type> → open directly on that type's form.
 *   - ?new=1 → open on the intent picker (no initialType).
 */
export function CreateGoalModal({
  open,
  onClose,
  onCreated,
  initialType,
  initialTitle,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (goalId: string) => void;
  /** If provided, skip the intent picker and open this type's form directly. */
  initialType?: CreateGoalType | null;
  /** Prefill the title (e.g. from a detection nudge). */
  initialTitle?: string;
}) {
  const [, navigate] = useLocation();
  const toast = useToast();
  const { spaces } = useSpaces();

  const [step, setStep] = React.useState<"pick" | "form">("pick");
  const [type, setType] = React.useState<CreateGoalType>("find_home");
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [locationId, setLocationId] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  // Reset/seed state whenever the modal opens.
  React.useEffect(() => {
    if (!open) return;
    setDescription("");
    setLocationId("");
    setCreating(false);
    if (initialType) {
      setType(initialType);
      setStep("form");
    } else {
      setStep("pick");
    }
    setTitle(initialTitle ?? "");
  }, [open, initialType, initialTitle]);

  const choose = (t: CreateGoalType) => {
    setType(t);
    setStep("form");
  };

  const createGoal = async (extra?: { title?: string; description?: string | null; activityLocationId?: string | null }) => {
    const finalTitle = (extra?.title ?? title).trim();
    if (!finalTitle) return;
    setCreating(true);
    try {
      const created = await apiFetch<{ id: string }>("/api/organizing-goals", {
        method: "POST",
        body: JSON.stringify({
          title: finalTitle,
          description: extra?.description !== undefined ? extra.description : (description.trim() || null),
          activityLocationId: extra?.activityLocationId !== undefined ? extra.activityLocationId : (locationId || null),
          type,
        }),
      });
      toast.show("Goal created!", "success");
      onClose();
      onCreated(created.id);
    } catch (err: any) {
      toast.show(err.message || "Failed to create goal", "error");
    }
    setCreating(false);
  };

  const activeIntent = INTENT_CARDS.find((c) => c.value === type)!;

  return (
    <Modal open={open} onClose={onClose} title={step === "pick" ? "What do you want to do?" : activeIntent.label}>
      {step === "pick" ? (
        <div className="space-y-2">
          <p className="text-sm text-body mb-1">Pick an intent — each is its own flow.</p>
          {INTENT_CARDS.map((card) => {
            const Icon = card.icon;
            const assistive = card.value === "declutter";
            return (
              <button
                key={card.value}
                onClick={() => choose(card.value)}
                className={`w-full text-left flex items-center gap-3 rounded-lg border border-clay-border transition-colors p-3 group ${assistive ? "hover:border-sage-deep/40" : "hover:border-primary/40"}`}
              >
                <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 transition-colors ${assistive ? "bg-sage-wash" : "bg-amethyst-soft"}`}>
                  <Icon className={`w-5 h-5 ${card.iconClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[1.0625rem] font-semibold text-ink">{card.label}</div>
                  <div className="text-[0.8125rem] text-faint">{card.hint}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Back to intent picker (only when we didn't deep-link straight to a type) */}
          {!initialType && (
            <button onClick={() => setStep("pick")} className="flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />Change intent
            </button>
          )}

          {/* What happens next — makes the type choice feel consequential (C2) */}
          <div className={`flex items-start gap-2.5 p-3 rounded-md border border-clay-border ${type === "declutter" ? "bg-sage-wash" : "bg-amethyst-soft"}`}>
            <activeIntent.icon className={`w-4 h-4 shrink-0 mt-0.5 ${activeIntent.iconClass}`} />
            <p className="text-sm text-body"><span className="font-medium text-ink">Next: </span>{activeIntent.next}</p>
          </div>

          {type === "find_home" && (
            <FindHomeForm
              title={title}
              setTitle={setTitle}
              description={description}
              setDescription={setDescription}
              locationId={locationId}
              setLocationId={setLocationId}
              spaces={spaces}
              creating={creating}
              onSubmit={() => createGoal()}
            />
          )}

          {type === "reorganize" && (
            <ReorganizeForm
              title={title}
              setTitle={setTitle}
              locationId={locationId}
              setLocationId={setLocationId}
              spaces={spaces}
              creating={creating}
              onSubmit={() => createGoal()}
            />
          )}

          {type === "declutter" && (
            <DeclutterForm
              title={title}
              setTitle={setTitle}
              spaces={spaces}
              creating={creating}
              onReviewEverything={() => { onClose(); navigate("/organize/declutter"); }}
              onFocusedSession={(sessionTitle, spaceId) =>
                createGoal({ title: sessionTitle, description: null, activityLocationId: spaceId || null })
              }
            />
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Find a home — items-first: prompt for the items + optional activity location.
// (Item linking happens on the detail page; here we capture title + location.)
// ---------------------------------------------------------------------------
function FindHomeForm({
  title, setTitle, description, setDescription, locationId, setLocationId, spaces, creating, onSubmit,
}: {
  title: string; setTitle: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  locationId: string; setLocationId: (v: string) => void;
  spaces: { id: string; name: string }[];
  creating: boolean; onSubmit: () => void;
}) {
  return (
    <>
      <div>
        <label className="text-sm font-medium text-body mb-1 block flex items-center gap-1"><Package className="w-3.5 h-3.5" />What needs a home?</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., 'The scissors' or 'Kids' art supplies'"
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          maxLength={200}
          autoFocus
        />
        <p className="text-[0.8125rem] text-faint mt-1">You'll link the specific items on the next screen.</p>
      </div>
      <div>
        <label className="text-sm font-medium text-body mb-1 block">Details (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What's frustrating about this?"
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
          rows={2}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-body mb-1 block flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Where does this happen? (optional)</label>
        <select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          <option value="">Skip for now</option>
          {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <SubmitButton disabled={!title.trim() || creating} creating={creating} onClick={onSubmit} label="Create goal" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Reorganize — space-first: prompt for the target space; items optional.
// ---------------------------------------------------------------------------
function ReorganizeForm({
  title, setTitle, locationId, setLocationId, spaces, creating, onSubmit,
}: {
  title: string; setTitle: (v: string) => void;
  locationId: string; setLocationId: (v: string) => void;
  spaces: { id: string; name: string }[];
  creating: boolean; onSubmit: () => void;
}) {
  // When a space is chosen and no title yet, offer a sensible default title.
  const spaceName = spaces.find((s) => s.id === locationId)?.name;
  const effectiveTitle = title.trim() || (spaceName ? `Reorganize ${spaceName}` : "");

  return (
    <>
      <div>
        <label className="text-sm font-medium text-body mb-1 block flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Which space isn't working?</label>
        <select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          autoFocus
        >
          <option value="">Choose a space…</option>
          {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-medium text-body mb-1 block">Goal name</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={spaceName ? `Reorganize ${spaceName}` : "e.g., 'Reorganize the pantry'"}
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          maxLength={200}
        />
        <p className="text-[0.8125rem] text-faint mt-1">Items are optional — you can add containers to move on the next screen.</p>
      </div>
      <SubmitButton
        disabled={!effectiveTitle || creating}
        creating={creating}
        onClick={() => { if (!title.trim() && spaceName) setTitle(`Reorganize ${spaceName}`); onSubmit(); }}
        label="Create goal"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Declutter — two modes: review everything (global tool) OR a focused,
// space/category-scoped session (creates a declutter goal). No activity location.
// ---------------------------------------------------------------------------
function DeclutterForm({
  title, setTitle, spaces, creating, onReviewEverything, onFocusedSession,
}: {
  title: string; setTitle: (v: string) => void;
  spaces: { id: string; name: string }[];
  creating: boolean;
  onReviewEverything: () => void;
  onFocusedSession: (title: string, spaceId: string) => void;
}) {
  const [mode, setMode] = React.useState<"choose" | "focused">("choose");
  const [spaceId, setSpaceId] = React.useState("");
  const spaceName = spaces.find((s) => s.id === spaceId)?.name;
  const effectiveTitle = title.trim() || (spaceName ? `Declutter ${spaceName}` : "");

  if (mode === "choose") {
    return (
      <div className="space-y-2">
        <button
          onClick={onReviewEverything}
          className="w-full text-left flex items-center gap-3 rounded-lg border border-clay-border hover:border-sage-deep/40 transition-colors p-3"
        >
          <div className="w-10 h-10 rounded-md bg-sage-wash flex items-center justify-center shrink-0"><Recycle className="w-5 h-5 text-sage-deep" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[1.0625rem] font-semibold text-ink">Review everything</div>
            <div className="text-[0.8125rem] text-faint">Go through all unused items across every space. No goal needed.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-faint shrink-0" />
        </button>
        <button
          onClick={() => setMode("focused")}
          className="w-full text-left flex items-center gap-3 rounded-lg border border-clay-border hover:border-primary/40 transition-colors p-3"
        >
          <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0"><Target className="w-5 h-5 text-primary" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[1.0625rem] font-semibold text-ink">Focus on a space or category</div>
            <div className="text-[0.8125rem] text-faint">A saved, named session scoped to one area. This creates a declutter goal.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-faint shrink-0" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button onClick={() => setMode("choose")} className="flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" />Back
      </button>
      <div>
        <label className="text-sm font-medium text-body mb-1 block flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Scope (optional)</label>
        <select
          value={spaceId}
          onChange={(e) => setSpaceId(e.target.value)}
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          <option value="">Whole home / a category</option>
          {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-medium text-body mb-1 block">Session name</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={spaceName ? `Declutter ${spaceName}` : "e.g., 'Declutter the winter clothes'"}
          className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          maxLength={200}
        />
      </div>
      <SubmitButton
        disabled={!effectiveTitle || creating}
        creating={creating}
        onClick={() => onFocusedSession(effectiveTitle, spaceId)}
        label="Start focused declutter"
      />
    </>
  );
}

function SubmitButton({ disabled, creating, onClick, label }: { disabled: boolean; creating: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-2"
    >
      {creating ? <><Loader2 className="w-4 h-4 animate-spin" />Creating…</> : label}
    </button>
  );
}
